import { MutableRefObject, useLayoutEffect, useRef, useState } from 'react'

import { DotBadge, FloatingPanel, TextBadge } from 'ui/components'

import { useViewPortUpdateListener } from '../../../../hooks'

import * as SC from './styles'

type Error = {
  fieldName: string
  error: string
  label: string | undefined
}

type Props = {
  submitButtonRef: MutableRefObject<HTMLButtonElement | null>
  errors: Error[]
}

const adjustPanelPosition = (buttonRef: any, panelRef: any) => {
  if (buttonRef?.current) {
    const { top, right } = buttonRef.current.getBoundingClientRect()
    if (panelRef?.current) {
      panelRef.current.style.bottom = window.innerHeight - top + 20 + 'px'
      panelRef.current.style.right = window.innerWidth - right - 20 + 'px'
    }
  }
}

export const ErrorPanel = (props: Props) => {
  const { errors, submitButtonRef } = props

  const ref = useRef<HTMLDivElement>(null)
  const [submitClicked, setSubmitClicked] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const [errorsToRemove, setErrorsToRemove] = useState<Error[]>([])
  const [mappedErrors, setMappedErrors] = useState<Error[]>([])

  useLayoutEffect(() => {
    const button = submitButtonRef.current
    const onClick = () => {
      setSubmitClicked(true)
    }
    if (button) {
      button.addEventListener('click', onClick)
    }
    return () => {
      if (button) {
        button.removeEventListener('click', onClick)
      }
    }
  }, [submitButtonRef])

  useLayoutEffect(() => {
    if (submitClicked && errors.length) {
      setShowErrors(true)
    } else if (errors.length > 1) {
      setShowErrors(true)
    } else if (!errors.length) {
      setShowErrors(false)
    }

    const elements = document.querySelectorAll(`[data-field-with-error]`)
    const newMappedErrors: Error[] = []

    elements.forEach((el: HTMLElement) => {
      const ind = errors.findIndex((err) => err.fieldName === el.dataset.fieldWithError)
      if (ind !== -1) {
        newMappedErrors.push({
          fieldName: errors[ind].fieldName,
          error: errors[ind].error,
          label: el.dataset.fieldlabel
        })
      }
    })

    const removedErrors = mappedErrors.filter(
      (error) => !newMappedErrors.find((el) => el.fieldName === error.fieldName)
    )

    if (removedErrors.length) {
      setErrorsToRemove(removedErrors)
      const timer = setTimeout(() => {
        setMappedErrors(newMappedErrors)
        setErrorsToRemove([])
      }, 250)

      return () => clearTimeout(timer)
    } else {
      setMappedErrors(newMappedErrors)
    }

    return () => {}
  }, [JSON.stringify(errors), submitClicked])

  useViewPortUpdateListener(() => {
    adjustPanelPosition(submitButtonRef, ref)
  }, true)

  return (
    <FloatingPanel
      ref={ref}
      isEnabled={showErrors}
      title={'Unresolved Errors'}
      actions={<TextBadge color={'error'}>{errors.length} ERRORS</TextBadge>}
      content={
        <SC.ErrorsWrapper>
          {mappedErrors.map((el) => {
            const field = document.querySelector(`[data-field-with-error="${el.fieldName}"]`)
            return (
              <SC.Error
                key={el.fieldName}
                className={errorsToRemove.includes(el) ? 'removing' : ''}
                onClick={() => {
                  field?.scrollIntoView({ block: 'center', behavior: 'smooth' })
                }}
              >
                <DotBadge color={errorsToRemove.includes(el) ? 'default' : 'error'} />
                <SC.ErrorMessageWrapper>
                  <SC.ErrorFieldName>{el.label}</SC.ErrorFieldName>
                  <SC.ErrorMessage>{el.error}</SC.ErrorMessage>
                </SC.ErrorMessageWrapper>
              </SC.Error>
            )
          })}
        </SC.ErrorsWrapper>
      }
      onEnabled={(isEnabled) => {
        if (isEnabled) {
          adjustPanelPosition(submitButtonRef, ref)
        }
      }}
    />
  )
}
